import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { Alert } from 'components/Toast';
import { toast } from 'react-toastify';
import { services } from 'service';
import { Payments, WillHistoryDto } from 'service/dto/customer.dto';
import { ArchivedCustomerDto, CustomerListType } from 'service/dto/staff.dto';
import { UserInfoDto } from 'service/dto/user.dto';
import { ApiPaginationOptions, DashStats } from 'types/general.type';

type QueryParams = {
  whereOptions: any;
  paginationOptions: ApiPaginationOptions;
  enabled: boolean;
};

export const useGetCustomerList = ({
  whereOptions,
  paginationOptions,
  enabled
}: QueryParams) => {
  const customerList = useQuery<CustomerListType, AxiosError<Error>>(
    ['customer_list', paginationOptions, whereOptions],
    () =>
      services.manageCustomerService.getCustomers(
        whereOptions,
        paginationOptions
      ),

    {
      keepPreviousData: true,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      enabled
    }
  );
  return customerList;
};

export const useGetCustomerDetails = ({
  customerId
}: {
  customerId: number;
}) => {
  return useQuery<UserInfoDto, AxiosError<Error>>(
    ['get_customer', customerId],
    () => services.manageCustomerService.getCustomer(customerId),
    {
      keepPreviousData: false,
      refetchOnWindowFocus: false,
      enabled: !!customerId
    }
  );
};

export const useGetArchivedCustomer = ({
  customerId,
  enabled
}: {
  customerId: number;
  enabled: boolean;
}) => {
  return useQuery<ArchivedCustomerDto, AxiosError<Error>>(
    ['get_archived_customer', customerId],
    () => services.manageCustomerService.getArchivedCustomer(customerId),
    {
      keepPreviousData: false,
      refetchOnWindowFocus: false,
      enabled: enabled
    }
  );
};

export const useDeactiveCustomer = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({
      note,
      customerId
    }: {
      note: string;
      customerId: number;
    }): Promise<any> => {
      return services.manageCustomerService.deactivateCustomer({
        note,
        customerId
      });
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['deactivate-customer']);
      },
      onError: (error: Error) => {
        toast.error(<Alert message={error.message} type="error" />);
      }
    }
  );
};

export const useActivateCustomer = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ customerId }: { customerId: number }): Promise<any> => {
      return services.manageCustomerService.activateCustomer(customerId);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['activate-customer']);
      },
      onError: (error: Error) => {
        toast.error(<Alert message={error.message} type="error" />);
      }
    }
  );
};

export const useArchiveCustomer = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({
      payload,
      customerId
    }: {
      payload: FormData;
      customerId: number;
    }): Promise<any> => {
      return services.manageCustomerService.archiveCustomer(
        payload,
        customerId
      );
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['archive-customer']);
      },
      onError: (error: Error) => {
        toast.error(<Alert message={error.message} type="error" />);
      }
    }
  );
};

export const useGetCustomerStats = ({ customerId }: { customerId: number }) => {
  return useQuery<DashStats, AxiosError<Error>>(
    ['get_customer_stats', customerId],
    () => services.manageCustomerService.getCustomerStats(customerId),
    {
      keepPreviousData: false,
      refetchOnWindowFocus: false,
      enabled: !!customerId
    }
  );
};

export const useGetCustomerActivities = ({
  customerId
}: {
  customerId: number;
}) =>
  useQuery(
    ['customer_activities', customerId],
    () => services.manageCustomerService.getActivities(customerId),
    {
      refetchOnWindowFocus: false
    }
  );

export const useGetCustomerBankList = ({
  customerId
}: {
  customerId: number;
}) =>
  useQuery(
    ['customer_banks', customerId],
    () => services.manageCustomerService.getCustomerBanks(customerId),
    {
      refetchOnWindowFocus: false
    }
  );

export const useGetCustomerPensionList = ({
  customerId
}: {
  customerId: number;
}) =>
  useQuery(
    ['customer_pension_accounts', customerId],
    () => services.manageCustomerService.getCustomerPensionAccounts(customerId),
    {
      refetchOnWindowFocus: false
    }
  );

export const useGetCustomerBondList = ({
  customerId
}: {
  customerId: number;
}) =>
  useQuery(
    ['customer_bonds', customerId],
    () => services.manageCustomerService.getCustomerBonds(customerId),
    {
      refetchOnWindowFocus: false
    }
  );

export const useGetCustomerStockList = ({
  customerId
}: {
  customerId: number;
}) =>
  useQuery(
    ['customer_stocks', customerId],
    () => services.manageCustomerService.getCustomerStocks(customerId),
    {
      refetchOnWindowFocus: false
    }
  );

export const useGetCustomerPhysicalList = ({
  customerId
}: {
  customerId: number;
}) =>
  useQuery(
    ['customer_physicals', customerId],
    () => services.manageCustomerService.getCustomerPhysicals(customerId),
    {
      refetchOnWindowFocus: false
    }
  );

export const useGetCustomerBenefitList = ({
  customerId
}: {
  customerId: number;
}) =>
  useQuery(
    ['customer_benefits', customerId],
    () => services.manageCustomerService.getCustomerBenefits(customerId),
    {
      refetchOnWindowFocus: false
    }
  );

export const useApproveRejectCustomerWill = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({
      note,
      isApproved,
      customerId,
      staffId
    }: {
      note: string;
      isApproved: boolean;
      customerId: number;
      staffId: number;
    }): Promise<any> => {
      return services.manageCustomerService.approveRejectWill({
        note,
        isApproved,
        customerId,
        staffId
      });
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['approve_reject_customer_will']);
      },
      onError: (error: Error) => {
        toast.error(<Alert message={error.message} type="error" />);
      }
    }
  );
};

export const useAssignStaffToCustomer = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({
      staffId,
      customerId
    }: {
      staffId: number;
      customerId: number;
    }): Promise<any> => {
      return services.manageCustomerService.assignStaff(staffId, customerId);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['assign-staff']);
      },
      onError: (error: Error) => {
        toast.error(<Alert message={error.message} type="error" />);
      }
    }
  );
};

export const useGetCustomerPayments = ({
  whereOptions,
  paginationOptions,
  enabled
}: QueryParams) => {
  const customerList = useQuery<Payments, AxiosError<Error>>(
    ['customer_payments', paginationOptions, whereOptions],
    () =>
      services.manageCustomerService.getCustomerPayments(
        whereOptions,
        paginationOptions
      ),

    {
      keepPreviousData: true,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      enabled
    }
  );
  return customerList;
};

export const useGetCustomerPayment = ({ paymentId }: { paymentId: number }) =>
  useQuery(
    ['customer_payment', paymentId],
    () => services.manageCustomerService.paymentDetails(paymentId),
    {
      refetchOnWindowFocus: false
    }
  );

export const useGetCustomerInfoDetails = ({
  customerId
}: {
  customerId: number;
}) =>
  useQuery(
    ['customer_info_stats', customerId],
    () => services.manageCustomerService.getCustomerInfoDetails(customerId),
    {
      refetchOnWindowFocus: false
    }
  );

export const useUploadSignedWill = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ formData }: { formData: FormData }): Promise<any> => {
      return services.manageCustomerService.uploadSignedWill(formData);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['upload_customer_will']);
      },
      onError: (error: Error) => {
        toast.error(<Alert message={error.message} type="error" />);
      }
    }
  );
};

export const useGetCustomerWillList = ({
  whereOptions,
  paginationOptions,
  enabled
}: QueryParams) => {
  const customerList = useQuery<WillHistoryDto, AxiosError<Error>>(
    ['customer_will_list', paginationOptions, whereOptions],
    () =>
      services.manageCustomerService.getCustomerWills(
        whereOptions,
        paginationOptions
      ),

    {
      keepPreviousData: true,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      enabled
    }
  );
  return customerList;
};

export const useAllowWillModification = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ customerId }: { customerId: number }): Promise<any> => {
      return services.manageCustomerService.allowWillModification(customerId);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['allow-will-modification']);
      },
      onError: (error: Error) => {
        toast.error(<Alert message={error.message} type="error" />);
      }
    }
  );
};

export const useViewGeneratedPDF = (userId: number, willId: number) => {
  return useQuery(
    ['view-generated-pdf', willId],
    () => services.manageCustomerService.viewGeneratedPDF(userId, willId),
    {
      refetchOnWindowFocus: false,
      keepPreviousData: true,
      enabled: !!userId && !!willId,
      onError: (error: Error) => {
        toast.error(<Alert message={error.message} type="error" />);
      }
    }
  );
};
